import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm, FieldData, TextField, WebhookEvent } from 'vopay';
import * as Yup from 'yup';
import { Toggle } from '#shared/components/UI';
import useBuildUpdateWebhookRows from './buildUpdateWebhooksRows.helper';

// One field per event holding its URL, plus a matching "<event>Enabled" boolean field for the toggle.
export type WebhookFormValues = {
  [K in WebhookEvent as `${K}Enabled`]: boolean;
} & {
  [K in WebhookEvent]: string;
};

type UseUpdateWebhooksFormConfigArgs = {
  events: readonly WebhookEvent[];
  initialValues: WebhookFormValues;
  onSubmit: (values: WebhookFormValues) => void | Promise<void>;
  readOnly?: boolean;
};

export default function useUpdateWebhooksFormConfig({
  events,
  initialValues,
  onSubmit,
  readOnly = false,
}: UseUpdateWebhooksFormConfigArgs) {
  const webhookRows = useBuildUpdateWebhookRows();
  const { t } = useTranslation('translations', { keyPrefix: 'Shared.Webhooks.Update.Form' });

  // buildUpdateWebhooksRows.helper.tsx keys rows by the UpdateWebhookId enum; its values are the
  // same strings as WebhookEvent, so look up by that shared string value, not the nominal type.
  const rowByEvent = useMemo(
    () => new Map(webhookRows.map((row) => [String(row.id) as WebhookEvent, row])),
    [webhookRows],
  );

  const config = useMemo<FieldData<WebhookFormValues>[]>(
    () =>
      events.flatMap((event) => {
        // Synthesized so enable/disable lives in the same Formik-driven values as the URL field
        // (old code tracked toggle state in separate local React state, outside Formik).
        const enabledKey = `${event}Enabled` as const;
        const preset = rowByEvent.get(event)?.preset ?? 'text';

        return [
          {
            fieldName: enabledKey,
            getInputState: () => (readOnly ? 'disabled' : 'optional'),
            showField: () => true,
            renderField: ({ getFormikData }) => {
              const { value, disabled, onChange, name } = getFormikData<boolean>({});

              return <Toggle name={name} checked={value} readOnly={disabled} onChange={onChange} />;
            },
          },
          {
            fieldName: event,
            getInputState: (ctx) => (!readOnly && ctx.values[enabledKey] ? 'required' : 'disabled'),
            showField: () => true,
            validate: () =>
              Yup.string()
                .url(t('invalidURLValidation', { defaultValue: 'Please enter a valid URL.' }))
                .required(),
            renderField: ({ getFormikData }) => (
              <TextField
                preset={preset}
                variant="outlineShadow"
                placeholder={t('urlPlaceholder', { defaultValue: 'Provide URL' })}
                containerWidth="100%"
                {...getFormikData<string>({})}
              />
            ),
          },
        ];
      }),
    [events, rowByEvent, readOnly, t],
  );

  const { ctx, renderFormElements } = useForm<WebhookFormValues>({
    config,
    initialValues,
    enableReinitialize: true,
    onSubmit,
  });

  // renderFormElements returns one opaque React element wrapping whatever fields you hand it —
  // not an indexable array of per-field nodes — so each table cell needs its own call with a
  // single-field config, rather than one call for the whole row sliced apart afterward.
  const configByFieldName = useMemo(() => new Map(config.map((field) => [field.fieldName, field])), [config]);

  const rows = events.map((event) => {
    const enabledField = configByFieldName.get(`${event}Enabled`)!;
    const urlField = configByFieldName.get(event)!;

    return {
      event,
      label: rowByEvent.get(event)?.label ?? event,
      isEnabled: Boolean(ctx.values[`${event}Enabled`]),
      toggleElement: renderFormElements({ config: [enabledField], override: (fieldData) => fieldData.template }),
      urlElement: renderFormElements({ config: [urlField], override: (fieldData) => fieldData.template }),
    };
  });

  return { ctx, rows };
}
